import { ChangeDetectionStrategy, Component, ElementRef, LOCALE_ID, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { combineLatest, firstValueFrom, map, Observable, shareReplay, startWith } from 'rxjs';
import { PageService } from '../../../app/providers/page.service';
import { CountryLanguageUrls, DivisionTitleColor, SettingsService } from '../../../app/providers/settings.service';
import { CountryNameService } from '../../../app/providers/country-name.service';
import { LanguageUrls, isPageWithCountries } from '../../../app/types/page';
import { Router } from '@angular/router';
import { CommonModule, isPlatformServer } from '@angular/common';
import { DIALOG_POLYFILL_SERVICE_TOKEN } from '../../../app/providers/dialog-polyfill.service';
import { FormsModule } from '@angular/forms';
import { OverlayFigureComponent } from '@knorr-bremse-portals/ngx-components/components/overlay-figure';
import { PictureComponent } from '@knorr-bremse-portals/ngx-components/components/picture';
import { ProgressIndicatorComponent } from '@knorr-bremse-portals/ngx-components/components/progress-indicator';
import { Language, LanguageSelectComponent } from '@knorr-bremse-portals/ngx-components/components/language-select';
import { provideRegionSelectIntl, Region, RegionSelectComponent } from '@knorr-bremse-portals/ngx-components/components/region-select';
import { CardComponent } from '@knorr-bremse-portals/ngx-components/components/card';
import { GLOBAL_REGION } from '../../providers/settings-constants';
import { RouterOrExternalLinkDirective } from '@knorr-bremse-portals/ngx-components';
import { StandaloneLinkComponent } from '@knorr-bremse-portals/ngx-components/components/standalone-link';
import { iconSearch } from '@knorr-bremse-portals/icons';
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon';
import { LanguageNameService } from '../../providers/language-name.service';
import {
  HeaderSearchIntl,
  NavigationContentComponent,
  NavigationLevel,
  HeaderComponent as KBHeaderComponent,
  SiteNavigationComponent,
  provideSiteNavigationIntl,
  provideHeaderSearchIntl,
} from '@knorr-bremse-portals/ngx-components/components/header';
import { HeaderTitleComponent } from '@knorr-bremse-portals/ngx-components/components/header-title';
import { LocalSiteNavigationIntl } from '../../providers/intl/site-navigation.intl';
import { LocalRegionSelectIntl } from '../../providers/intl/region-select.intl';
import { LocalHeaderSearchIntl } from '../../providers/intl/header-search.intl';
import { EditorIdDirective } from '../../directives/editor-id.directive';

export enum HeaderTitleColor {
  Primary  = 'primary',
  Secondary = 'secondary',
}

const ColorMap = {
  [DivisionTitleColor.Blue]: HeaderTitleColor.Primary,
  [DivisionTitleColor.Gray]: HeaderTitleColor.Secondary,
};

type HeaderTitle = {
  label: string;
  url?: string;
  color: HeaderTitleColor;
};

@Component({
    selector: 'app-header',
    styleUrls: ['./header.component.scss'],
    templateUrl: './header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        OverlayFigureComponent,
        PictureComponent,
        ProgressIndicatorComponent,
        CardComponent,
        NavigationContentComponent,
        RouterOrExternalLinkDirective,
        StandaloneLinkComponent,
        IconComponent,
        KBHeaderComponent,
        HeaderTitleComponent,
        EditorIdDirective,
        RegionSelectComponent,
        LanguageSelectComponent,
        SiteNavigationComponent,
    ],
    providers: [
        provideSiteNavigationIntl(LocalSiteNavigationIntl),
        provideRegionSelectIntl(LocalRegionSelectIntl),
        provideHeaderSearchIntl(LocalHeaderSearchIntl),
    ],
})
export class HeaderComponent {

  private pageService = inject(PageService);
  private settingsService = inject(SettingsService);
  private countryNameService = inject(CountryNameService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private dialogPolyfillService = inject(DIALOG_POLYFILL_SERVICE_TOKEN);
  private languageNameService = inject(LanguageNameService);

  protected editorialSettingsEditorId = this.settingsService.footer.pipe(
    map((footer) => footer.editorId),
  );

  protected logo$ = combineLatest([
    this.settingsService.logo,
    this.settingsService.homeUrl,
  ]).pipe(
    map(([logoUrl, homeUrl]) => {
      return {
        src: logoUrl,
        alt: 'Logo',
        href: homeUrl,
      }
    }),
  );

  protected iconSearch = iconSearch;
  protected headerSearchIntl = inject(HeaderSearchIntl);

  protected navigation$: Observable<NavigationLevel> = this.settingsService.navigation;

  protected navigationCards$ = this.settingsService.navigationCards;

  @ViewChild('regionSelectElement', { read: ElementRef }) private set regionSelectElement(regionSelectElementRef: ElementRef<HTMLElement>) {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    if (!regionSelectElementRef) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.dialogPolyfillService.registerDialog(regionSelectElementRef.nativeElement.querySelector('dialog')!);
  }

  protected regionSelector$: Observable<{
    currentLanguage: Language;
    currentRegion: Region;
    availableRegions: Region[];
  } | null> = combineLatest([
    this.pageService.page.pipe(startWith(undefined)),
    this.settingsService.startPageCountryLanguages,
  ]).pipe(
    map(([page, startPageCountryLanguages]) => {
      if (!page) {
        return null;
      }
      if (!isPageWithCountries(page)) {
        return null;
      }

      const allCombinations = {...page.countryLanguageCombinations};
      for (const [region, languages] of Object.entries(startPageCountryLanguages)) {
        if (region === GLOBAL_REGION) {
          continue;
        }

        if (allCombinations[region] === undefined) {
          allCombinations[region] = {};
        }
        const languageMap = Object.fromEntries(languages.map((languageCode) => ([languageCode])));
        allCombinations[region] = {...languageMap, ...allCombinations[region]};
      }

      const regions = this.toRegions(allCombinations);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const currentRegion = regions.find(region => region.code === page.currentCountry)!;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const currentLanguage = currentRegion.languages.find(language => language.code === page.currentLanguage)!;

      return {
        currentLanguage: currentLanguage,
        currentRegion: currentRegion,
        availableRegions: regions,
      }
    }),
  );

  protected languages$: Observable<Language[]> = this.pageService.languageUrls.pipe(
    map((languageUrls) => this.toLanguages(languageUrls)),
    shareReplay(1),
  );

  private localeId = inject(LOCALE_ID);

  protected selectedLanguage$: Observable<Language> = this.languages$.pipe(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map(languages => languages.find(language => language.code === this.localeId)!),
  );

  protected showGlobalSearch$ = this.settingsService.showGlobalSearch;

  protected titles$: Observable<HeaderTitle[]> = combineLatest([
    combineLatest([
      this.settingsService.division,
      this.settingsService.divisionColor,
    ]).pipe(startWith(undefined)),
    this.pageService.localContentLink.pipe(startWith(undefined)),
  ]).pipe(
    map(([division, localContentLink]) => {
      const titles: HeaderTitle[] = [];
      if (division) {
        const [divisionTitle, divisionColor] = division;
        titles.push({
          label: divisionTitle,
          color: ColorMap[divisionColor],
        });
      }
      if (localContentLink) {
        titles.push({
          label: localContentLink.label,
          url: localContentLink.url,
          color: division && ColorMap[division[1]] === HeaderTitleColor.Secondary ? HeaderTitleColor.Primary : HeaderTitleColor.Secondary,
        });
      }

      return titles;
    }),
  );

  private toLanguage(languageCode: string): Language {
    return {
      code: languageCode,
      nativeName: this.languageNameService.getNativeName(languageCode),
    }
  }
  private toLanguages(languageUrls: LanguageUrls): Language[] {
    return Object.entries(languageUrls).map(([languageCode]) => {
      return this.toLanguage(languageCode);
    });
  }
  private toRegions(countryLanguageUrls: CountryLanguageUrls): Region[] {
    return Object.entries(countryLanguageUrls).map(([countryCode, languageUrls]) => {
      return {
        code: countryCode,
        name: this.countryNameService.getCombinedName(countryCode),
        languages: this.toLanguages(languageUrls),
      }
    });
  }

  private readonly searchUrlCommands$ = this.regionSelector$.pipe(
    map((regionSelector) => {
      const commands = [this.localeId];
      if (regionSelector) {
        commands.push(regionSelector.currentRegion.code.toLowerCase());
      }
      commands.push('search');

      return commands;
    }),
  );

  protected searchUrl$ = this.searchUrlCommands$.pipe(
    map((commands) => {
      return `/${commands.join('/')}/`;
    }),
  );

  protected async onSelectedLanguageChange(language: Language): Promise<void> {
    if (language.code === this.localeId) {
      return;
    }

    const languageUrls = await firstValueFrom(this.pageService.languageUrls);
    const languageCodeAndUrl = Object.entries(languageUrls).find(([languageCode]) => (
      languageCode === language.code
    ));
    if (languageCodeAndUrl) {
      this.router.navigateByUrl(languageCodeAndUrl[1]);
    }
  }

  protected async onRegionSelect(selected: {region: Region; language: Language}): Promise<void> {
    const page = await firstValueFrom(this.pageService.page);
    if (!isPageWithCountries(page)) {
      return;
    }

    const languageCombinations = Object.entries(page.countryLanguageCombinations).find(([regionCode]) => {
      return regionCode === selected.region.code;
    });
    if (!languageCombinations) {
      this.router.navigate([selected.language.code, selected.region.code.toLowerCase()], {
        queryParamsHandling: 'preserve',
      });
      return;
    }

    const languageCodeAndUrl = Object.entries(languageCombinations[1]).find(([languageCode]) => (
      languageCode === selected.language.code
    ));
    if (!languageCodeAndUrl) {
      this.router.navigate([selected.language.code, selected.region.code.toLowerCase()], {
        queryParamsHandling: 'preserve',
      });
      return;
    }

    this.router.navigateByUrl(languageCodeAndUrl[1]);
  }

}
